import { useCallback } from "react";
import { toNumber } from "../utils/numberUtils";

export default function useAssetWorkflow({
  companyApi,
  companyId,
  canEditCompany,
  assetForm,
  setAssetForm,
  setAssetSaveState,
  setAssetDialogMode,
  setEditingAssetId,
  setAddAssetOpen,
  onAssetSaved,
  defaultAssetForm,
  editingAssetId,
}) {
  const buildAssetPayload = useCallback(() => {
    const payload = {};
    if (assetForm.asset_name.trim()) payload.asset_name = assetForm.asset_name.trim();
    if (assetForm.jurisdiction) payload.jurisdiction = toNumber(assetForm.jurisdiction);
    if (assetForm.reporting_currency) payload.reporting_currency = assetForm.reporting_currency;
    if (assetForm.ownership_type) payload.ownership_type = assetForm.ownership_type;
    if (assetForm.ownership_percentage !== "")
      payload.ownership_percentage = toNumber(assetForm.ownership_percentage);
    if (assetForm.lifecycle) payload.lifecycle = assetForm.lifecycle;
    if (assetForm.lifecycle_stage) payload.lifecycle_stage = assetForm.lifecycle_stage;
    if (assetForm.start_year_of_operation !== "")
      payload.start_year_of_operation = toNumber(assetForm.start_year_of_operation);
    if (assetForm.expected_first_production_year !== "")
      payload.expected_first_production_year = toNumber(assetForm.expected_first_production_year);
    if (assetForm.closure_or_reclamation_year !== "")
      payload.closure_or_reclamation_year = toNumber(assetForm.closure_or_reclamation_year);
    payload.has_historic_production = Boolean(assetForm.has_historic_production);
    if (assetForm.asset_page_url) payload.asset_page_url = assetForm.asset_page_url.trim();
    if (assetForm.latitude !== "") payload.latitude = toNumber(assetForm.latitude);
    if (assetForm.longitude !== "") payload.longitude = toNumber(assetForm.longitude);
    if (assetForm.geological_region) payload.geological_region = assetForm.geological_region.trim();
    if (assetForm.mining_district) payload.mining_district = assetForm.mining_district.trim();
    if (assetForm.land_size_acres !== "")
      payload.land_size_acres = toNumber(assetForm.land_size_acres);
    if (assetForm.pct_land_explored !== "")
      payload.pct_land_explored = toNumber(assetForm.pct_land_explored);
    return payload;
  }, [assetForm]);

  const handleOpenAddAsset = useCallback(() => {
    setAssetForm(defaultAssetForm);
    setAssetSaveState({ loading: false, error: null, success: null });
    setAssetDialogMode("create");
    setEditingAssetId(null);
    setAddAssetOpen(true);
  }, [
    defaultAssetForm,
    setAddAssetOpen,
    setAssetDialogMode,
    setAssetForm,
    setAssetSaveState,
    setEditingAssetId,
  ]);

  const handleOpenEditAsset = useCallback(
    (asset) => {
      if (!asset) return;
      setAssetForm({
        asset_name: asset.asset_name || "",
        jurisdiction: asset.jurisdiction?.id || asset.jurisdiction || "",
        reporting_currency: asset.reporting_currency || "USD",
        ownership_type: asset.ownership_type || "Owner/Operator",
        ownership_percentage: asset.ownership_percentage ?? "",
        lifecycle: asset.lifecycle || "na",
        lifecycle_stage: asset.lifecycle_stage || "",
        start_year_of_operation: asset.start_year_of_operation ?? "",
        expected_first_production_year: asset.expected_first_production_year ?? "",
        closure_or_reclamation_year: asset.closure_or_reclamation_year ?? "",
        has_historic_production: Boolean(asset.has_historic_production),
        asset_page_url: asset.asset_page_url || "",
        latitude: asset.latitude ?? "",
        longitude: asset.longitude ?? "",
        geological_region: asset.geological_region || "",
        mining_district: asset.mining_district || "",
        land_size_acres: asset.land_size_acres ?? "",
        pct_land_explored: asset.pct_land_explored ?? "",
      });
      setAssetSaveState({ loading: false, error: null, success: null });
      setAssetDialogMode("edit");
      setEditingAssetId(asset.id);
      setAddAssetOpen(true);
    },
    [setAddAssetOpen, setAssetDialogMode, setAssetForm, setAssetSaveState, setEditingAssetId]
  );

  const handleCloseAssetDialog = useCallback(() => {
    setAddAssetOpen(false);
    setAssetDialogMode("create");
    setEditingAssetId(null);
  }, [setAddAssetOpen, setAssetDialogMode, setEditingAssetId]);

  const handleAssetFieldChange = useCallback(
    (field) => (event) => {
      const value =
        event.target.type === "checkbox" ? event.target.checked : event.target.value;
      setAssetForm((prev) => ({
        ...prev,
        [field]: value,
        ...(field === "lifecycle" ? { lifecycle_stage: "" } : null),
      }));
    },
    [setAssetForm]
  );

  const handleSubmitAsset = useCallback(async () => {
    if (!companyId) return;
    if (!assetForm.asset_name.trim()) {
      setAssetSaveState({ loading: false, error: "Asset name is required.", success: null });
      return;
    }
    if (!assetForm.jurisdiction) {
      setAssetSaveState({ loading: false, error: "Jurisdiction is required.", success: null });
      return;
    }
    if ((assetForm.latitude === "") !== (assetForm.longitude === "")) {
      setAssetSaveState({
        loading: false,
        error: "Latitude and longitude must be provided together.",
        success: null,
      });
      return;
    }
    if (!canEditCompany) {
      setAssetSaveState({
        loading: false,
        error: "You need an approved claim to submit asset updates.",
        success: null,
      });
      return;
    }
    const payload = { ...buildAssetPayload(), company: companyId };
    setAssetSaveState({ loading: true, error: null, success: null });
    try {
      const created = await companyApi.createAsset(payload);
      setAssetSaveState({
        loading: false,
        error: null,
        success: "Asset saved as draft.",
      });
      setAddAssetOpen(false);
      if (onAssetSaved) {
        onAssetSaved(created);
      }
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.asset_name ||
        "Unable to submit asset.";
      setAssetSaveState({ loading: false, error: detail, success: null });
    }
  }, [
    assetForm.asset_name,
    assetForm.jurisdiction,
    assetForm.latitude,
    assetForm.longitude,
    buildAssetPayload,
    canEditCompany,
    companyApi,
    companyId,
    onAssetSaved,
    setAddAssetOpen,
    setAssetSaveState,
  ]);

  const handleUpdateAsset = useCallback(async () => {
    if (!editingAssetId) return;
    if (!assetForm.asset_name.trim()) {
      setAssetSaveState({ loading: false, error: "Asset name is required.", success: null });
      return;
    }
    if (!assetForm.jurisdiction) {
      setAssetSaveState({ loading: false, error: "Jurisdiction is required.", success: null });
      return;
    }
    if ((assetForm.latitude === "") !== (assetForm.longitude === "")) {
      setAssetSaveState({
        loading: false,
        error: "Latitude and longitude must be provided together.",
        success: null,
      });
      return;
    }
    if (!canEditCompany) {
      setAssetSaveState({
        loading: false,
        error: "You need an approved claim to submit asset updates.",
        success: null,
      });
      return;
    }
    const payload = buildAssetPayload();
    setAssetSaveState({ loading: true, error: null, success: null });
    try {
      const updated = await companyApi.updateAsset(editingAssetId, payload);
      setAssetSaveState({
        loading: false,
        error: null,
        success: "Asset updated.",
      });
      setAddAssetOpen(false);
      setAssetDialogMode("create");
      setEditingAssetId(null);
      if (onAssetSaved) {
        onAssetSaved(updated);
      }
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.asset_name ||
        "Unable to update asset.";
      setAssetSaveState({ loading: false, error: detail, success: null });
    }
  }, [
    assetForm.asset_name,
    assetForm.jurisdiction,
    assetForm.latitude,
    assetForm.longitude,
    buildAssetPayload,
    canEditCompany,
    companyApi,
    editingAssetId,
    onAssetSaved,
    setAddAssetOpen,
    setAssetDialogMode,
    setAssetSaveState,
    setEditingAssetId,
  ]);

  return {
    handleOpenAddAsset,
    handleOpenEditAsset,
    handleCloseAssetDialog,
    handleAssetFieldChange,
    handleSubmitAsset,
    handleUpdateAsset,
  };
}
